/**
 * Client for the NEW jordandraper.com rules API (the rebrand site).
 * Spec: WEB/public/docs/rules-integration.md. Everything is public,
 * read-only, CORS-open.
 *
 * The ONLY hardcoded host is the index endpoint — every other URL
 * (living-book JSON, PDFs, figures) comes out of API responses, so the
 * DNS cutover to jordandraper.com costs us nothing.
 *
 * Per the spec we do NOT mirror content: PDFs and figures are
 * deep-linked, and book JSON is cached in memory for the session only.
 */

// Production host. The DNS cutover landed 2026-08-09 and the old
// jordandrapergames.vercel.app now answers with a 308 to this domain —
// a CROSS-ORIGIN redirect, which the fetch can't follow, so every
// rules link broke until this constant moved. If the host ever changes
// again, change it HERE: it is the only hardcoded host in the app, and
// every other URL (living-book JSON, PDFs, figures) comes out of the
// API response, so they follow automatically.
export const RULES_INDEX_URL = 'https://jordandraper.com/api/v1/rules'

export interface ApiRulesetFile {
  language: string        // 'EN'
  languageName: string    // 'English'
  url: string
  official: boolean       // false = unofficial translation — LABEL IT (orange)
}

export interface ApiRuleset {
  id: string
  title: string
  designers?: string[]
  /** >1 entry = crossover between boxes; listed under every game involved. */
  games?: string[]
  yenChain?: boolean
  onlineExclusive?: boolean
  video?: string | null
  files?: ApiRulesetFile[]
}

export interface ApiGame {
  slug: string
  title: string
  printedRulebook: { url: string; note?: string } | null
  livingRulebook: { locales: string[]; html: string; json: string } | null
  rulesets: ApiRuleset[]
}

export interface RulesIndex {
  version: number
  updatedAt: string
  rulesLibrary: string
  games: ApiGame[]
}

export interface BookBlock {
  type: 'p' | 'list'
  text?: string
  items?: string[]
}

export interface BookFigure {
  url: string
  width?: number
  height?: number
  /** English block index the figure follows; absent = end of section. */
  after?: number
}

export interface BookSection {
  id: string             // stable join key across languages
  heading: string
  meta?: Record<string, string>
  blocks: BookBlock[]
  figures?: BookFigure[]
}

export interface LivingBook {
  slug: string
  locale: string
  requestedLocale: string
  /** true = this locale doesn't exist; the WHOLE book is English. Label it. */
  fallback: boolean
  availableLocales: string[]
  sections: BookSection[]
}

// ── Session caches ────────────────────────────────────────────────────
let indexCache: RulesIndex | null = null
const bookCache = new Map<string, LivingBook>()

export async function fetchRulesIndex(force = false): Promise<RulesIndex> {
  if (indexCache && !force) return indexCache
  const res = await fetch(RULES_INDEX_URL)
  if (!res.ok) throw new Error(`rules index: HTTP ${res.status}`)
  indexCache = await res.json() as RulesIndex
  return indexCache
}

export async function fetchLivingBook(jsonUrl: string, locale: string): Promise<LivingBook> {
  const key = `${jsonUrl}?locale=${locale}`
  const hit = bookCache.get(key)
  if (hit) return hit
  const res = await fetch(key)
  if (!res.ok) throw new Error(`living book: HTTP ${res.status}`)
  const book = await res.json() as LivingBook
  bookCache.set(key, book)
  return book
}

/**
 * Interleaves a section's figures among its blocks. `after` is an
 * ENGLISH block index; for translations the spec says to scale it by
 * the ratio of block counts (block lists aren't parallel across
 * languages — proportional placement is the intended approximation).
 */
export function figureSlots(
  section: BookSection,
  enBlockCount: number | null,
): Map<number, BookFigure[]> {
  const out = new Map<number, BookFigure[]>()
  const n = section.blocks.length
  for (const fig of section.figures ?? []) {
    let at = n - 1 // default: end of section
    if (typeof fig.after === 'number') {
      at = fig.after
      if (enBlockCount && enBlockCount > 0 && enBlockCount !== n) {
        at = Math.round(fig.after * (n / enBlockCount))
      }
      at = Math.max(0, Math.min(n - 1, at))
    }
    const arr = out.get(at) ?? []
    arr.push(fig)
    out.set(at, arr)
  }
  return out
}
